/**
 * User and Company entity types for storage
 * These match the domain entities from auth feature
 */
interface UserEntity {
  id: string;
  email: string;
  userName: string;
  role: string;
  isActive: boolean;
  dateCreated: string;
  dateLastLoggedIn: string | null;
  zohoRefreshToken?: string;
}

interface CompanyEntity {
  companyId: string;
  companyName: string;
  zohoOrgId: string;
  appUrl?: string;
  addressLine1: string;
  addressLine2?: string | null;
  companyGstEnabled: string;
  /**
   * ZOUR-19 — GST applies to documents dated on or after this `YYYY-MM-DD`.
   *
   * Null or absent means GST applies to documents of every date. Kept in the
   * stored company alongside the flag because the review and manual-create
   * pages have to resolve a document's GST mode from its own date without a
   * round trip, and because the GST settings panel re-stores this on save so
   * a change takes effect without signing out.
   */
  gstEnabledFrom?: string | null;
  state: string;
  country: string;
  pincode: string;
  gstNumber: string;
  panNumber: string;
  cinNumber: string;
  currency: string;
  msme: string;
  amountCompactUnit?:
    | "whole"
    | "thousand"
    | "lakh"
    | "million"
    | "crore"
    | "billion"
    | "lakhs"
    | "crores";
  amountCompactDecimals?: 1 | 2;
  /**
   * JSON string from backend (company_profile.amount_compact_suffix_json).
   * We keep it as-is for storage; UI parsing happens in formatter.
   */
  amountCompactSuffixJson?: string | null;
  accountingBackend?: 'zoho' | 'tally';
  vendorType?: string;
  vendor_type?: string; // Backend sends snake_case (vendor_lite or vendor_pro)
  createdAt: string;
}

/**
 * Auth Storage Service
 * Handles all localStorage operations for authentication
 * This is a shared service that can be used across features
 */

// Storage Keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token",
  USER_DATA: "user_data",
  COMPANY_DATA: "company_data",
  ZOHO_REFRESH_TOKEN: "zoho_refresh_token",
} as const;

/**
 * Store authentication token
 */
export const storeToken = (token: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  } catch (error) {
    console.error("Error storing token:", error);
    throw new Error("Failed to store authentication token");
  }
};

/**
 * Get stored authentication token
 */
export const getToken = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error) {
    console.error("Error getting token:", error);
    return null;
  }
};

/**
 * Remove authentication token
 */
export const removeToken = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error) {
    console.error("Error removing token:", error);
  }
};

/**
 * Store user data
 */
export const storeUser = (user: UserEntity): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  } catch (error) {
    console.error("Error storing user data:", error);
    throw new Error("Failed to store user data");
  }
};

/**
 * Get stored user data
 */
export const getUser = (): UserEntity | null => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (!userData) return null;

    return JSON.parse(userData) as UserEntity;
  } catch (error) {
    console.error("Error getting user data:", error);
    return null;
  }
};

/**
 * Update stored user data (partial update)
 */
export const updateUser = (updates: Partial<UserEntity>): void => {
  try {
    const currentUser = getUser();
    if (!currentUser) {
      throw new Error("No user data found to update");
    }

    const updatedUser = { ...currentUser, ...updates };
    storeUser(updatedUser);
  } catch (error) {
    console.error("Error updating user data:", error);
    throw new Error("Failed to update user data");
  }
};

/**
 * Remove user data
 */
export const removeUser = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  } catch (error) {
    console.error("Error removing user data:", error);
  }
};

/**
 * Store company data
 */
export const storeCompany = (company: CompanyEntity): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(company));
  } catch (error) {
    console.error("Error storing company data:", error);
    throw new Error("Failed to store company data");
  }
};

/**
 * Get stored company data
 */
export const getCompany = (): CompanyEntity | null => {
  try {
    const companyData = localStorage.getItem(STORAGE_KEYS.COMPANY_DATA);
    if (!companyData) return null;

    return JSON.parse(companyData) as CompanyEntity;
  } catch (error) {
    console.error("Error getting company data:", error);
    return null;
  }
};

/**
 * Remove company data
 */
export const removeCompany = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.COMPANY_DATA);
  } catch (error) {
    console.error("Error removing company data:", error);
  }
};

/**
 * Store Zoho tokens
 */
export const storeZohoTokens = (refreshToken: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.ZOHO_REFRESH_TOKEN, refreshToken);
  } catch (error) {
    console.error("Error storing Zoho tokens:", error);
    throw new Error("Failed to store Zoho tokens");
  }
};

/**
 * Get Zoho refresh token from user data
 */
export const getZohoRefreshToken = (): string | null => {
  try {
    // const user = getUser();
    // return user?.zohoRefreshToken || null;
    return localStorage.getItem("zoho_refresh_token") || null;
  } catch (error) {
    console.error("Error getting Zoho refresh token:", error);
    return null;
  }
};

/**
 * Remove Zoho tokens
 */
export const removeZohoTokens = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.ZOHO_REFRESH_TOKEN);
  } catch (error) {
    console.error("Error removing Zoho tokens:", error);
  }
};

/**
 * Check if user has Zoho authentication
 */
export const hasZohoAuth = (): boolean => {
  const zohoToken = getZohoRefreshToken();
  return !!zohoToken;
};

/**
 * Clear all authentication data
 */
export const clearAllAuthData = (): void => {
  removeToken();
  removeUser();
  removeCompany();
  removeZohoTokens();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  const user = getUser();
  return !!token && !!user;
};

// Export types for use by features
export type { UserEntity, CompanyEntity };
